<template lang="pug">
//- One chat message (WhatsApp, and any non-email channel) as WhatsApp shows
//- it. The ONE bubble: the inbox and ticket thread (ConversationThread) and
//- the CMS admins' window onto our desk (PlatformTicketsDrawer) render this,
//- so the three can never drift apart.
.message(:class="message.direction === 'outbound' ? 'is-outbound' : 'is-inbound'")
    .bubble
        .bubble-subject(v-if="message.subject") {{ message.subject }}
        //- A quoted reply (customer tapped a quick reply / swiped-to-reply)
        //- shows the original card above the text, like WhatsApp does.
        whats-app-card-preview.bubble-quote(
            v-if="message.repliedTo"
            compact
            :header-image="quotedCard.headerImage"
            :header="quotedCard.header"
            :body="quotedCard.body"
            :footer="quotedCard.footer"
        )
        //- Image attachments render inline on top (WhatsApp shows the media
        //- above the caption); non-image files stay a paperclip link below.
        .bubble-media(v-if="imageAttachments.length")
            a.bubble-media__item(
                v-for="file in imageAttachments"
                :key="file.id"
                :href="file.signed_url || file.url"
                target="_blank"
                rel="noopener"
            )
                img(:src="file.signed_url || file.url" :alt="file.name")
        //- The WhatsApp card (header/body/footer/buttons) via the SHARED
        //- renderer in its in-bubble variant, so the bubble matches every
        //- other preview and URL buttons stay tappable.
        whats-app-card-preview.bubble-card(
            bubble
            link-buttons
            :header-image="template('header_media')"
            :header="template('header')"
            :body="html"
            :footer="template('footer')"
            :buttons="template('buttons') || []"
        )
        .bubble-attachments(v-if="fileAttachments.length")
            a.attachment(v-for="file in fileAttachments" :key="file.id" :href="file.signed_url || file.url" target="_blank" rel="noopener")
                i.el-icon-fa-paperclip.fas
                | {{ file.name }}
        .bubble-foot
            span.author(v-if="author") {{ author }}
            span.time {{ time }}
            //- WhatsApp-style delivery ticks (outbound only): ✓ sent,
            //- ✓✓ delivered, ✓✓ blue read, clock pending, ! failed.
            el-tooltip(v-if="message.direction === 'outbound'" :content="statusTooltip" placement="top")
                //- Only the DOUBLE tick is drawn: WhatsApp's overlaps and
                //- no icon font carries that (Font Awesome's check-double
                //- sets the two side by side). Its stroke copies
                //- fa-check's geometry — 45° down, 45° up, the up-stroke
                //- twice as long — so it sits beside the single tick as
                //- the same mark, twice. currentColor keeps the tone
                //- classes in charge of the colour.
                svg.bubble-tick.bubble-tick--double(
                    v-if="isDoubleTick"
                    :class="message.status === 'read' ? 'is-read' : 'is-delivered'"
                    viewBox="0 0 15.2 11"
                    fill="none"
                )
                    path(d="M1 6.1 3.9 9 9.7 3.2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round")
                    path(d="M5.5 6.1 8.4 9 14.2 3.2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round")
                i.bubble-tick(v-else :class="statusTick")
</template>

<i18n>
{
    "de": {
        "status.draft": "Entwurf",
        "status.queued": "In Warteschlange",
        "status.sent": "Gesendet",
        "status.delivered": "Zugestellt",
        "status.read": "Gelesen",
        "status.failed": "Fehlgeschlagen",
        "status.received": "Empfangen",
        "yyyy-MM-dd": "dd.MM.yyyy"
    },
    "en": {
        "status.draft": "Draft",
        "status.queued": "Queued",
        "status.sent": "Sent",
        "status.delivered": "Delivered",
        "status.read": "Read",
        "status.failed": "Failed",
        "status.received": "Received"
    }
}
</i18n>

<script>
import _ from 'lodash';

import { timestamp2date } from '@/filters';
import { toHtml as whatsappToHtml } from '@/extensions/communication/utils/whatsappMarkdown';
import WhatsAppCardPreview from '@/extensions/communication/views/Partials/WhatsAppCardPreview';

const STATUS_TICKS = {
    draft: 'el-icon-fa-clock far is-pending',
    queued: 'el-icon-fa-clock far is-pending',
    sent: 'el-icon-fa-check fas is-sent',
    delivered: 'el-icon-fa-check-double fas is-delivered',
    read: 'el-icon-fa-check-double fas is-read',
    failed: 'el-icon-fa-triangle-exclamation fas is-failed',
};

const isImage = (file) => (file.mime || '').startsWith('image/');

const KNOWN_STATUSES = ['draft', 'queued', 'sent', 'delivered', 'read', 'failed', 'received'];

export default {
    name: 'ChatBubble',
    components: {
        'whats-app-card-preview': WhatsAppCardPreview,
    },
    props: {
        message: {
            type: Object,
            required: true,
        },
        // Who wrote it, under the text. The thread names the agent; a
        // surface that must not (the CMS's window onto our desk) passes
        // its own wording, or nothing.
        author: {
            type: String,
            default: '',
        },
    },
    computed: {
        // Render WhatsApp's plain-text markers (*bold*, _italic_, ~strike~, ```mono```)
        // as formatting so the bubble shows what the customer sees. HTML is escaped
        // first, so only these markers ever produce tags (XSS-safe).
        html() {
            return whatsappToHtml(this.message.body_plain || this.message.body || '');
        },
        imageAttachments() {
            return (this.message.attachments || []).filter(isImage);
        },
        fileAttachments() {
            return (this.message.attachments || []).filter((file) => !isImage(file));
        },
        // The quoted (replied-to) message rendered as a compact card, so the reply
        // reproduces the original's header/body/footer like WhatsApp's quote.
        quotedCard() {
            const quoted = this.message.repliedTo;

            return {
                headerImage: _.get(quoted, 'templateComponents.header_media') || '',
                header: _.get(quoted, 'templateComponents.header') || '',
                body: _.get(quoted, 'body') || '',
                footer: _.get(quoted, 'templateComponents.footer') || '',
            };
        },
        time() {
            const timestamp = this.message.created_at;

            return timestamp ? timestamp2date(timestamp, this.$t('yyyy-MM-dd') + ', HH:mm') : '';
        },
        isDoubleTick() {
            return this.message.status === 'delivered' || this.message.status === 'read';
        },
        statusTick() {
            return STATUS_TICKS[this.message.status] || STATUS_TICKS.sent;
        },
        // The tick's tooltip: the status, plus WHY it failed (the driver's
        // status_reason), so "Failed: (#131053) Media upload error", not a bare tick.
        statusTooltip() {
            const status = this.message.status;
            // Exactly the thread's former label: a status we have no word for shows as itself.
            const label = KNOWN_STATUSES.includes(status) ? this.$t(`status.${status}`) : status;
            const reason = status === 'failed' ? _.get(this.message, 'status_reason') : '';

            return reason ? `${label}: ${reason}` : label;
        },
    },
    methods: {
        // WhatsApp template card parts stored on the message (null for plain
        // messages), rendered around the body so the bubble matches the real card.
        template(part) {
            return _.get(this.message, `templateComponents.${part}`) || '';
        },
    },
};
</script>

<style lang="sass" scoped>
.message
    position: relative
    display: flex
    margin-bottom: 8px
    .bubble
        position: relative
        // Clip bled media + button rows to the rounded corners.
        overflow: hidden
        // WhatsApp caps a bubble around 65% of the pane; a long unbroken string then wraps.
        max-width: min(65%, 30rem)
        padding: 6px 9px 8px
        border-radius: 7.5px
        font-family: -apple-system, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif
        font-size: 14.2px
        line-height: 19px
        color: #111b21
        box-shadow: 0 1px .5px rgba(11, 20, 26, .13)
        white-space: pre-wrap
        // Break a long unbroken string (a URL, a token) mid-word so it wraps inside the bubble.
        overflow-wrap: anywhere
        word-break: break-word
    .bubble-quote
        margin-bottom: 5px
    // Inline image attachments render on top and bleed to the bubble edges; the bubble's
    // overflow: hidden rounds the corners.
    .bubble-media
        margin: -6px -9px 5px
        .bubble-media__item
            display: block
            img
                display: block
                width: 100%
                max-height: 360px
                object-fit: cover
    // The message card (header/body/footer/buttons) is the shared WhatsAppCardPreview in its
    // in-bubble variant; it inherits the bubble's WhatsApp typography.
    .bubble-card
        display: block
    .bubble-subject
        font-weight: 600
        margin-bottom: 4px
    // WhatsApp meta, bottom-right: time + delivery tick.
    .bubble-foot
        display: flex
        align-items: center
        justify-content: flex-end
        gap: 4px
        margin-top: 2px
        font-size: 11px
        line-height: 15px
        color: #667781
        .bubble-tick
            font-size: 15px
            line-height: 1
            &--double
                width: 15px
                height: 11px
                vertical-align: -1px
            &.is-pending, &.is-sent, &.is-delivered
                color: #8696a0
            &.is-read
                color: #53bdeb
            &.is-failed
                color: #f56c6c
    .bubble-attachments
        margin-top: 5px
        font-size: 13px
        .attachment
            display: inline-block
            color: #027eb5
            text-decoration: none
            &:hover
                text-decoration: underline
            i
                margin-right: 5px
    // Incoming (left, white) / outgoing (right, green). Fully rounded corners, no tail.
    &.is-inbound
        justify-content: flex-start
        .bubble
            background: #fff
    &.is-outbound
        justify-content: flex-end
        .bubble
            background: #d9fdd3
</style>
